import { DataTable } from '@/shared/components/tables/DataTable';
import { useDataTableState } from '@/shared/components/tables/useDataTableState';
import { FindPersonFiltersDto } from '@/api.types.extra';
import { ReadPersonDto } from '@/api.types';
import { readPerson } from '../../person.service';
import { useEffect, useMemo, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';

type TipoPessoaIndex = 'todos' | 'defensor' | 'estagiario' | 'colaborador' | 'usuario';
type StatusAtivoFiltro = 'todos' | 'ativos' | 'inativos';
type PerfilFiltro = '' | 'defensor' | 'estagiario' | 'colaborador' | 'usuario' | 'sem_perfil';

interface Props {
  tipo: TipoPessoaIndex;
  columns: import('@/shared/components/tables/DataTable').Column<ReadPersonDto>[];
}

function isAtivoByTipo(row: ReadPersonDto, tipo: TipoPessoaIndex): boolean | null {
  switch (tipo) {
    case 'defensor':
      // Perfil ausente: trata como ativo (compatível com join só de ativos).
      return row.defensor?.ativo ?? true;
    case 'estagiario':
      return row.estagiario?.ativo ?? true;
    case 'usuario':
      return row.usuario?.ativo ?? true;
    case 'colaborador':
      return row.colaborador?.ativo ?? true;
    case 'todos': {
      const hasPerfil = Boolean(
        row.defensor || row.estagiario || row.colaborador || row.usuario,
      );
      // Sem perfil: fora do filtro de ativos/inativos (use o checkbox "Sem perfil").
      if (!hasPerfil) return null;
      return Boolean(
        row.defensor?.ativo ||
        row.estagiario?.ativo ||
        row.colaborador?.ativo ||
        row.usuario?.ativo,
      );
    }
    default:
      return true;
  }
}

export function PeopleDataTable({ tipo, columns }: Props) {
  const tableState = useDataTableState('nome');

  const [registros, setRegistros] = useState<[ReadPersonDto[], number]>();
  const [statusAtivo, setStatusAtivo] = useState<StatusAtivoFiltro>('todos');
  const [perfilFiltro] = useState<PerfilFiltro>('');
  const [letraInicial, setLetraInicial] = useState<string>('');
  const [semPerfil, setSemPerfil] = useState<boolean>(false);
  const [showLetterModal, setShowLetterModal] = useState(false);

  const filtro = tableState.filter;
  const quickBtnStyle = useMemo(() => ({ height: 31 }), []);

  const needsAtivoFilter = statusAtivo !== 'todos';
  const fetchModeServerSide = !needsAtivoFilter;
  const pageKey = fetchModeServerSide ? tableState.currentPage : 1;
  const pageSizeKey = fetchModeServerSide ? tableState.pageSize : 0;

  async function handleBuscar() {
    const filters: FindPersonFiltersDto = {
      skip: fetchModeServerSide ? (tableState.currentPage - 1) * tableState.pageSize : 0,
      take: fetchModeServerSide ? tableState.pageSize : 5000,
      filtro: filtro.trim() || undefined,
      letraInicial: letraInicial || undefined,
      excluirSomenteCandidato: tipo === 'todos' && !perfilFiltro ? true : undefined,
      comTelefone: true,
      orderBy: tableState.sortConfig.direction,
    };

    if (tipo === 'todos' && perfilFiltro) {
      filters.perfil = perfilFiltro;
      if (perfilFiltro === 'defensor') filters.comDefensor = true;
      if (perfilFiltro === 'estagiario') {
        filters.comEstagiario = true;
        filters.comEstagios = false;
      }
      if (perfilFiltro === 'colaborador') filters.comColaborador = true;
      if (perfilFiltro === 'usuario') filters.comUsuario = true;
    } else if (tipo === 'todos') {
      filters.semPerfil = semPerfil || undefined;
      // Para filtrar ativos/inativos na visão geral, carrega todos os perfis (incl. inativos).
      if (needsAtivoFilter) {
        filters.comDefensor = true;
        filters.comEstagiario = true;
        filters.comColaborador = true;
        filters.comUsuario = true;
        filters.incluirPerfisInativos = true;
      }
    } else {
      // Join só do tipo da página (não envia com* dos demais).
      if (tipo === 'defensor') {
        filters.comDefensor = true;
      } else if (tipo === 'estagiario') {
        filters.comEstagiario = true;
        filters.comEstagios = true;
      } else if (tipo === 'colaborador') {
        filters.comColaborador = true;
      } else if (tipo === 'usuario') {
        filters.comUsuario = true;
      }

      // Sempre via EXISTS (perfil) + join com inativos: is* faz INNER JOIN em
      // `ativo = true` e esconderia inativos mesmo em "Ativos e inativos".
      filters.incluirPerfisInativos = true;
      filters.perfil = tipo;
    }

    try {
      const res = await readPerson(filters);
      setRegistros(res);
    } catch (error) {
      toast.error(String(error));
    }
  }

  useEffect(() => {
    // DataTable dispara onChange no modo serverSide quando page/sort/filter mudam.
    // Aqui garantimos recarregar quando filtros "externos" mudam.
    handleBuscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, statusAtivo, perfilFiltro, letraInicial, semPerfil, fetchModeServerSide, pageKey, pageSizeKey, tableState.sortConfig?.key, tableState.sortConfig?.direction, filtro]);

  const filteredByAtivo = useMemo(() => {
    if (!registros) return undefined;
    if (statusAtivo === 'todos') return registros;

    const wantAtivo = statusAtivo === 'ativos';
    const rows = registros[0].filter((r) => {
      const ativo = isAtivoByTipo(r, tipo);
      if (ativo === null) return false;
      return ativo === wantAtivo;
    });
    return [rows, rows.length] as [ReadPersonDto[], number];
  }, [registros, statusAtivo, tipo]);

  return (
    <>
      <Modal
        centered
        show={showLetterModal}
        onHide={() => setShowLetterModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Filtrar por letra</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className={`btn btn-sm ${letraInicial ? 'btn-outline-secondary' : 'btn-secondary'}`}
              title="Remover filtro por letra"
              onClick={() => {
                setLetraInicial('');
                tableState.setCurrentPage(1);
                setShowLetterModal(false);
              }}
            >
              Todos
            </button>

            {'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('').map((l) => (
              <button
                key={l}
                type="button"
                className={`btn btn-sm ${letraInicial === l ? 'btn-primary' : 'btn-outline-primary'}`}
                style={{ width: 36, paddingLeft: 0, paddingRight: 0 }}
                title={`Listar nomes que começam com ${l}`}
                onClick={() => {
                  setLetraInicial(l);
                  tableState.setCurrentPage(1);
                  setShowLetterModal(false);
                }}
              >
                {l}
              </button>
            ))}
          </div>
          {letraInicial && (
            <div className="text-muted mt-3">
              Filtro atual: <strong>{letraInicial}</strong>
            </div>
          )}
        </Modal.Body>
      </Modal>

      {(filteredByAtivo || registros) && (
        <DataTable
          key={tipo}
          columns={columns}
          data={(filteredByAtivo ?? registros)?.[0]}
          initialSortKey="nome"
          serverSide={fetchModeServerSide}
          states={tableState}
          totalRecords={(filteredByAtivo ?? registros)?.[1]}
          showFilter={true}
          renderFilters={() => (
            <div className="d-flex align-items-center gap-2 flex-nowrap flex-shrink-0">
              <div className="btn-group btn-group-sm" role="group" aria-label="Filtros rápidos">
                <button
                  type="button"
                  className={`btn d-flex align-items-center justify-content-center ${letraInicial ? 'btn-primary' : 'btn-outline-primary'}`}
                  style={quickBtnStyle}
                  title="Abrir filtro por letra"
                  onClick={() => setShowLetterModal(true)}
                >
                  {letraInicial ? `Letra: ${letraInicial}` : 'Filtrar por letra'}
                </button>

                {tipo === 'todos' && (
                  <>
                    <input
                      type="checkbox"
                      className="btn-check"
                      id="semPerfil"
                      autoComplete="off"
                      checked={semPerfil}
                      disabled={Boolean(perfilFiltro)}
                      onChange={(e) => {
                        setSemPerfil(e.target.checked);
                        tableState.setCurrentPage(1);
                      }}
                    />
                    <label
                      className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                      style={quickBtnStyle}
                      htmlFor="semPerfil"
                      title="Listar apenas pessoas sem vínculo (sem defensor/estagiário/colaborador/usuário)."
                    >
                      Sem perfil
                    </label>
                  </>
                )}
              </div>

              <select
                className="form-select form-select-sm mb-0"
                style={{ width: 180, height: 31 }}
                value={statusAtivo}
                onChange={(e) => {
                  setStatusAtivo(e.target.value as StatusAtivoFiltro);
                  tableState.setCurrentPage(1);
                }}
                title="Filtrar por status (ativo/inativo)."
              >
                <option value="todos">Ativos e inativos</option>
                <option value="ativos">Somente ativos</option>
                <option value="inativos">Somente inativos</option>
              </select>
            </div>
          )}
        />
      )}
    </>
  );
}

